import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  BriefingSummary,
  createLink,
  initBriefingUpload,
  LinkAdmin,
  LinkMode,
  listBriefings,
  uploadToPresignedUrl,
} from "../api";
import {
  BRACKET_INNER,
  PLACEHOLDER_INNER,
  SCRIPT_TOKEN_REGEX,
  distributeTiming,
  emojify,
  parseScript,
  relativeTime,
} from "../utils";

const ACCEPTED_MIME = [
  "video/mp4",
  "video/webm",
  "video/quicktime",
  "video/x-matroska",
];

const DEFAULT_SCRIPT = `Bienvenue ! Prends une seconde pour respirer.
Quand tu te sens prêt·e, appuie sur le bouton rouge :)
---
(3) Je m'appelle ...(votre nom)...
Je suis [coach sportif]
() J'habite à Paris
(5) Je veux ...(votre objectif)...`;

type BriefingSource = "none" | "upload" | "existing";

export interface NewLinkSeed {
  title?: string;
  text?: string;
  mode?: LinkMode;
  recipient_name?: string;
  recipient_email?: string;
  max_duration_sec?: number;
  expires_in_days?: number;
  briefing_video_s3_key?: string;
}

interface Props {
  onCreated: () => void;
  seed?: NewLinkSeed | null;
}

export default function AdminNewLink({ onCreated, seed }: Props) {
  const nav = useNavigate();

  // Form fields — initialized from seed if a duplication was requested
  const [title, setTitle] = useState(seed?.title ?? "");
  const [text, setText] = useState(seed?.text ?? DEFAULT_SCRIPT);
  const [mode, setMode] = useState<LinkMode>(seed?.mode ?? "public");
  const [recipientName, setRecipientName] = useState(seed?.recipient_name ?? "");
  const [recipientEmail, setRecipientEmail] = useState(
    seed?.recipient_email ?? ""
  );
  const [maxDuration, setMaxDuration] = useState(seed?.max_duration_sec ?? 20);
  const [expiresInDays, setExpiresInDays] = useState<number | "">(
    seed?.expires_in_days ?? ""
  );

  // Briefing state — pre-fill from seed's existing briefing key if any
  const [briefingSource, setBriefingSource] = useState<BriefingSource>(
    seed?.briefing_video_s3_key ? "existing" : "none"
  );
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [briefingFile, setBriefingFile] = useState<File | null>(null);
  const [briefingPreview, setBriefingPreview] = useState<string | null>(null);
  const [uploadPct, setUploadPct] = useState<number | null>(null);
  const [briefingKey, setBriefingKey] = useState<string | null>(
    seed?.briefing_video_s3_key ?? null
  );
  const [existingBriefings, setExistingBriefings] = useState<BriefingSummary[]>(
    []
  );

  // Submission state
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [created, setCreated] = useState<LinkAdmin | null>(null);
  const [copied, setCopied] = useState(false);

  // Auto-avatar selection (preview UI — not yet sent to backend).
  // Will be wired to CreateLinkRequest in Bloc 2 (auto_avatar flag).
  const [autoAvatar, setAutoAvatar] = useState(false);
  const [autoAvatarPersons, setAutoAvatarPersons] = useState<string[]>(["mara"]);
  const [autoAvatarBgs, setAutoAvatarBgs] = useState<string[]>(["studio"]);
  const [autoAvatarLangs, setAutoAvatarLangs] = useState<string[]>(["fr"]);

  const toggleInArray = (arr: string[], v: string, min = 1): string[] => {
    if (arr.includes(v)) {
      const next = arr.filter((x) => x !== v);
      return next.length >= min ? next : arr;
    }
    return [...arr, v];
  };

  const autoJobCount =
    autoAvatarPersons.length * autoAvatarBgs.length * autoAvatarLangs.length;

  // Load existing briefings on mount (for the dropdown).
  // If we were seeded with an existing briefing key, populate its preview URL too.
  useEffect(() => {
    listBriefings()
      .then((bs) => {
        setExistingBriefings(bs);
        if (briefingKey && !briefingPreview) {
          const match = bs.find((b) => b.s3_key === briefingKey);
          if (match) setBriefingPreview(match.preview_url);
        }
      })
      .catch(() => {
        /* ignore, user can still upload a fresh one */
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pickFile = () => fileInputRef.current?.click();

  const onFile = async (f: File) => {
    if (!ACCEPTED_MIME.includes(f.type)) {
      setErr(`Format non supporté (${f.type}). MP4 / MOV / WebM / MKV.`);
      return;
    }
    if (f.size > 500 * 1024 * 1024) {
      setErr("Fichier trop gros (>500 MB). Compresse d'abord.");
      return;
    }
    setErr(null);
    setBriefingFile(f);
    setBriefingPreview(URL.createObjectURL(f));
    setBriefingKey(null);
    setUploadPct(0);

    try {
      const init = await initBriefingUpload(f.type);
      await uploadToPresignedUrl(init.upload_url, f, f.type, setUploadPct);
      setBriefingKey(init.s3_key);
      setUploadPct(100);
    } catch (e: any) {
      setErr(e.message || "Upload du briefing échoué");
      setUploadPct(null);
    }
  };

  const chooseExisting = (s3_key: string) => {
    const b = existingBriefings.find((x) => x.s3_key === s3_key);
    setBriefingKey(s3_key);
    setBriefingPreview(b?.preview_url ?? null);
    setBriefingFile(null);
    setUploadPct(null);
  };

  const resetBriefing = () => {
    setBriefingFile(null);
    setBriefingPreview(null);
    setBriefingKey(null);
    setUploadPct(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr(null);
    if (mode === "nominative" && !recipientEmail) {
      setErr("Email destinataire requis en mode nominatif");
      return;
    }
    if (uploadPct !== null && uploadPct < 100) {
      setErr("Attends la fin de l'upload du briefing");
      return;
    }

    setSubmitting(true);
    try {
      const link = await createLink({
        title: title.trim() || undefined,
        text,
        mode,
        recipient_name: recipientName || undefined,
        recipient_email: recipientEmail || undefined,
        max_duration_sec: maxDuration,
        expires_in_days:
          expiresInDays === "" ? undefined : Number(expiresInDays),
        briefing_video_s3_key: briefingKey || undefined,
      });
      setCreated(link);
      await navigator.clipboard.writeText(link.url);
      setCopied(true);
    } catch (e: any) {
      if (String(e.message).includes("401")) nav("/admin/login");
      else if (String(e.message).includes("409"))
        setErr("This title is already taken — pick a different one.");
      else setErr(e.message || "Erreur à la création");
    } finally {
      setSubmitting(false);
    }
  };

  const copy = async () => {
    if (!created) return;
    await navigator.clipboard.writeText(created.url);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const waShare = () => {
    if (!created) return;
    const who = created.recipient_name ? `Hey ${created.recipient_name}, ` : "";
    const msg = `${who}record me a video on "${created.title}" → ${created.url}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank");
  };

  const reset = () => {
    setTitle("");
    setText(DEFAULT_SCRIPT);
    setMode("public");
    setRecipientName("");
    setRecipientEmail("");
    setMaxDuration(20);
    setExpiresInDays("");
    setBriefingSource("none");
    resetBriefing();
    setCreated(null);
    setCopied(false);
  };

  // ── Success screen ────────────────────────────────────────────────
  if (created) {
    return (
      <section className="max-w-xl mx-auto space-y-6">
        <div className="text-center space-y-2">
          <div className="text-5xl">✓</div>
          <h2 className="text-2xl font-bold">Link ready</h2>
          <p className="text-concrete">
            <b>{created.title}</b> — copied to your clipboard.
          </p>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-4 space-y-3">
          <input
            readOnly
            value={created.url}
            className="w-full px-3 py-2 text-sm bg-ivory rounded border border-stone font-mono"
            onFocus={(e) => e.currentTarget.select()}
          />
          <div className="flex gap-2">
            <button
              onClick={copy}
              className="flex-1 py-2 bg-stone text-charcoal rounded hover:bg-ash transition"
            >
              {copied ? "✓ Copied" : "Copy again"}
            </button>
            <button
              onClick={waShare}
              className="flex-1 py-2 bg-[#25D366] text-white rounded hover:brightness-95 transition font-medium"
            >
              Share via WhatsApp
            </button>
          </div>
        </div>

        <div className="flex gap-2 justify-center text-sm">
          <button
            onClick={reset}
            className="px-4 py-2 text-concrete hover:text-charcoal underline"
          >
            Create another link
          </button>
          <span className="text-concrete">·</span>
          <button
            onClick={onCreated}
            className="px-4 py-2 text-concrete hover:text-charcoal underline"
          >
            Back to links
          </button>
        </div>
      </section>
    );
  }

  // ── Form ──────────────────────────────────────────────────────────
  return (
    <form onSubmit={submit} className="max-w-2xl mx-auto space-y-6">
      <h2 className="text-2xl font-bold">New link</h2>

      {/* Title */}
      <div className="space-y-1">
        <label className="text-sm font-medium">
          Title{" "}
          <span className="text-concrete font-normal">
            (must be unique — leave empty for a random fruit name)
          </span>
        </label>
        <input
          type="text"
          maxLength={80}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="e.g. Vivatech 2026, or leave empty"
          className="w-full px-3 py-2 border border-stone rounded-lg focus:outline-none focus:border-charcoal"
        />
      </div>

      {/* Mode */}
      <div className="space-y-1">
        <label className="text-sm font-medium">Access</label>
        <div className="flex gap-2 flex-wrap">
          {(["public", "nominative"] as LinkMode[]).map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => setMode(m)}
              className={`px-4 py-2 rounded-lg text-sm transition ${
                mode === m
                  ? "bg-charcoal text-ivory"
                  : "bg-stone text-concrete hover:bg-ash"
              }`}
            >
              {m === "public"
                ? "Public (reusable)"
                : "Nominative (single recipient)"}
            </button>
          ))}
        </div>
      </div>

      {mode === "nominative" && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <input
            type="text"
            placeholder="Recipient name"
            value={recipientName}
            onChange={(e) => setRecipientName(e.target.value)}
            className="px-3 py-2 border border-stone rounded-lg focus:outline-none focus:border-charcoal"
          />
          <input
            type="email"
            required
            placeholder="recipient@example.com"
            value={recipientEmail}
            onChange={(e) => setRecipientEmail(e.target.value)}
            className="px-3 py-2 border border-stone rounded-lg focus:outline-none focus:border-charcoal"
          />
        </div>
      )}

      {/* Teleprompter text */}
      <div className="space-y-1">
        <label className="text-sm font-medium">
          Teleprompter script{" "}
          <span className="text-concrete font-normal">
            (one line per segment · wrap placeholders in{" "}
            <code className="bg-stone/60 px-1 rounded">...(...)...</code>)
          </span>
        </label>
        <textarea
          required
          minLength={1}
          maxLength={5000}
          rows={7}
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Je m'appelle ...(dire votre nom)..."
          className="w-full px-3 py-2 border border-stone rounded-lg focus:outline-none focus:border-charcoal font-mono text-sm"
        />
        <ScriptPreview text={text} maxSec={maxDuration} />
      </div>

      {/* Briefing video — source picker */}
      <div className="space-y-2">
        <label className="text-sm font-medium">
          Briefing video{" "}
          <span className="text-concrete font-normal">
            (optional — shown before the recorder hits Record)
          </span>
        </label>
        <div className="flex gap-2 flex-wrap">
          {(
            [
              { id: "none", label: "No briefing" },
              { id: "upload", label: "Upload new" },
              {
                id: "existing",
                label: `Pick existing${
                  existingBriefings.length > 0
                    ? ` (${existingBriefings.length})`
                    : ""
                }`,
              },
            ] as { id: BriefingSource; label: string }[]
          ).map((opt) => (
            <button
              key={opt.id}
              type="button"
              disabled={opt.id === "existing" && existingBriefings.length === 0}
              onClick={() => {
                setBriefingSource(opt.id);
                resetBriefing();
              }}
              className={`px-3 py-1.5 rounded-lg text-sm transition disabled:opacity-50 ${
                briefingSource === opt.id
                  ? "bg-charcoal text-ivory"
                  : "bg-stone text-concrete hover:bg-ash"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>

        {briefingSource === "upload" && (
          <>
            <input
              ref={fileInputRef}
              type="file"
              accept="video/mp4,video/webm,video/quicktime,video/x-matroska"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) onFile(f);
              }}
            />
            {!briefingFile && (
              <button
                type="button"
                onClick={pickFile}
                className="w-full py-6 border-2 border-dashed border-stone rounded-lg text-concrete hover:border-charcoal hover:text-charcoal transition"
              >
                + Choose a video file
              </button>
            )}
            {briefingFile && (
              <div className="bg-white rounded-lg p-3 space-y-2">
                {briefingPreview && (
                  <video
                    src={briefingPreview}
                    controls
                    className="w-full rounded bg-black max-h-64"
                  />
                )}
                <div className="flex items-center justify-between text-sm">
                  <span className="truncate">
                    {briefingFile.name} ·{" "}
                    {(briefingFile.size / 1e6).toFixed(1)} MB
                  </span>
                  <button
                    type="button"
                    onClick={resetBriefing}
                    className="text-red-600 hover:text-red-700"
                  >
                    Remove
                  </button>
                </div>
                {uploadPct !== null && (
                  <div className="space-y-1">
                    <div className="h-2 bg-stone rounded-full overflow-hidden">
                      <div
                        className="h-full bg-charcoal transition-all"
                        style={{ width: `${uploadPct}%` }}
                      />
                    </div>
                    <div className="text-xs text-concrete">
                      {uploadPct < 100
                        ? `Uploading ${uploadPct}%`
                        : "✓ Uploaded"}
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {briefingSource === "existing" && (
          <div className="bg-white rounded-lg p-3 space-y-3">
            <select
              value={briefingKey ?? ""}
              onChange={(e) => chooseExisting(e.target.value)}
              className="w-full px-3 py-2 border border-stone rounded-lg bg-ivory"
            >
              <option value="">— pick a briefing —</option>
              {existingBriefings.map((b) => (
                <option key={b.s3_key} value={b.s3_key}>
                  {b.s3_key.replace("briefings/", "")} · last used{" "}
                  {relativeTime(b.last_used_at)}
                </option>
              ))}
            </select>
            {briefingPreview && briefingKey && (
              <video
                src={briefingPreview}
                controls
                className="w-full rounded bg-black max-h-64"
              />
            )}
          </div>
        )}
      </div>

      {/* Auto-avatar (preview UI) */}
      <div className="space-y-2 rounded-lg border border-stone bg-paper p-3">
        <label className="flex items-start gap-2 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={autoAvatar}
            onChange={(e) => setAutoAvatar(e.target.checked)}
            className="mt-1"
          />
          <span>
            <span className="text-sm font-medium text-charcoal">
              Générer automatiquement un avatar après l&apos;enregistrement
            </span>
            <span className="block text-xs text-concrete mt-0.5">
              La sélection ci-dessous sera enfilée dans le pipeline dès que la vidéo est transcodée.
              Tu pourras toujours ajouter d&apos;autres versions depuis la page vidéo.
            </span>
          </span>
        </label>

        {autoAvatar && (
          <div className="space-y-3 pt-2 mt-2 border-t border-stone">
            {/* Personnages */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-[10.5px] font-mono uppercase text-concrete">
                  Personnages
                </span>
                <span className="text-[10.5px] font-mono text-concrete">
                  {autoAvatarPersons.length} sélectionné
                  {autoAvatarPersons.length > 1 ? "s" : ""}
                </span>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {["mara", "tomas", "yara"].map((pid) => {
                  const on = autoAvatarPersons.includes(pid);
                  return (
                    <button
                      key={pid}
                      type="button"
                      onClick={() =>
                        setAutoAvatarPersons((a) => toggleInArray(a, pid))
                      }
                      className={`h-8 px-3 text-xs font-mono rounded border ${
                        on
                          ? "bg-charcoal text-ivory border-charcoal"
                          : "border-stone bg-paper text-charcoal hover:border-charcoal"
                      }`}
                    >
                      {on ? "✓ " : ""}
                      {pid}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Fonds */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-[10.5px] font-mono uppercase text-concrete">
                  Fonds
                </span>
                <span className="text-[10.5px] font-mono text-concrete">
                  {autoAvatarBgs.length} sélectionné
                  {autoAvatarBgs.length > 1 ? "s" : ""}
                </span>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {["studio", "atelier", "desk"].map((bid) => {
                  const on = autoAvatarBgs.includes(bid);
                  return (
                    <button
                      key={bid}
                      type="button"
                      onClick={() =>
                        setAutoAvatarBgs((a) => toggleInArray(a, bid))
                      }
                      className={`h-8 px-3 text-xs font-mono rounded border ${
                        on
                          ? "bg-charcoal text-ivory border-charcoal"
                          : "border-stone bg-paper text-charcoal hover:border-charcoal"
                      }`}
                    >
                      {on ? "✓ " : ""}
                      {bid}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Langues */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-[10.5px] font-mono uppercase text-concrete">
                  Langues
                </span>
                <span className="text-[10.5px] font-mono text-concrete">
                  {autoAvatarLangs.length} sélectionnée
                  {autoAvatarLangs.length > 1 ? "s" : ""}
                </span>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {["fr", "en", "es", "de", "he"].map((code) => {
                  const on = autoAvatarLangs.includes(code);
                  return (
                    <button
                      key={code}
                      type="button"
                      onClick={() =>
                        setAutoAvatarLangs((a) => toggleInArray(a, code))
                      }
                      className={`h-8 px-3 text-xs font-mono uppercase rounded border ${
                        on
                          ? "bg-charcoal text-ivory border-charcoal"
                          : "border-stone bg-paper text-charcoal hover:border-charcoal"
                      }`}
                    >
                      {code}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Récap */}
            <div className="flex items-center justify-between rounded border border-charcoal bg-ivory-tint px-3 py-2">
              <span className="text-xs font-mono text-charcoal">
                {autoAvatarPersons.length} × {autoAvatarBgs.length} ×{" "}
                {autoAvatarLangs.length} avatars à générer
              </span>
              <span className="font-mono text-lg font-bold text-charcoal tabular-nums">
                {autoJobCount}
              </span>
            </div>

            <p className="text-[10.5px] text-concrete font-mono">
              Preview UI · le flag <code>auto_avatar</code> n&apos;est pas encore branché au
              backend ; la sélection n&apos;est pas envoyée à la création du lien pour
              l&apos;instant.
            </p>
          </div>
        )}
      </div>

      {/* Advanced */}
      <details className="bg-white rounded-lg p-3">
        <summary className="cursor-pointer text-sm font-medium">
          Advanced options
        </summary>
        <div className="mt-4 grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-concrete">
              Max duration (seconds)
            </label>
            <input
              type="number"
              min={10}
              max={300}
              value={maxDuration}
              onChange={(e) => setMaxDuration(parseInt(e.target.value) || 20)}
              className="w-full px-3 py-2 border border-stone rounded-lg"
            />
          </div>
          <div>
            <label className="text-xs text-concrete">Expires in (days)</label>
            <input
              type="number"
              min={1}
              max={90}
              placeholder="never"
              value={expiresInDays}
              onChange={(e) =>
                setExpiresInDays(
                  e.target.value === "" ? "" : parseInt(e.target.value)
                )
              }
              className="w-full px-3 py-2 border border-stone rounded-lg"
            />
          </div>
        </div>
      </details>

      {err && (
        <div className="text-sm text-red-600 bg-red-50 p-3 rounded-lg">
          {err}
        </div>
      )}

      <button
        type="submit"
        disabled={submitting || (uploadPct !== null && uploadPct < 100)}
        className="w-full py-3 bg-charcoal text-ivory rounded-lg font-bold hover:bg-black transition disabled:opacity-50"
      >
        {submitting ? "Generating…" : "Generate link"}
      </button>
    </form>
  );
}

// ── Script preview ────────────────────────────────────────────────────
function ScriptPreview({ text, maxSec }: { text: string; maxSec: number }) {
  const trimmed = text.trim();
  const parsed = parseScript(text);
  const timing = distributeTiming(parsed.segments, maxSec);
  const segCount = parsed.segments.length;

  return (
    <>
      <div className="flex justify-between text-xs text-concrete">
        <span>
          {segCount} segment(s)
          {parsed.intro.length > 0 && ` · ${parsed.intro.length} intro line(s)`}
        </span>
        <span>{text.length} / 5000</span>
      </div>

      {trimmed && (
        <div className="mt-3 rounded-xl bg-charcoal text-ivory p-4 space-y-3 border border-concrete">
          <div className="text-[10px] uppercase tracking-widest text-stone/60 font-semibold">
            Preview
          </div>

          {parsed.intro.length > 0 && (
            <div className="space-y-1 pb-2 border-b border-concrete/40">
              <div className="text-[9px] uppercase tracking-widest text-stone/40 font-semibold">
                Avant l&apos;enregistrement
              </div>
              {parsed.intro.map((line, i) => (
                <div
                  key={`intro-${i}`}
                  className="text-sm text-ivory/80 leading-relaxed"
                >
                  {renderPreviewLine(line)}
                </div>
              ))}
            </div>
          )}

          {parsed.segments.length > 0 && (
            <div className="space-y-2">
              {parsed.intro.length > 0 && (
                <div className="text-[9px] uppercase tracking-widest text-stone/40 font-semibold">
                  Pendant l&apos;enregistrement
                </div>
              )}
              {parsed.segments.map((seg, i) => (
                <div
                  key={`seg-${i}`}
                  className="flex items-baseline gap-3 text-base leading-relaxed"
                >
                  <span className="text-stone/50 font-mono text-xs tabular-nums w-10 shrink-0 text-right">
                    {timing[i] ? `${timing[i].dur.toFixed(1)}s` : ""}
                  </span>
                  <span className="font-semibold flex-1 whitespace-pre-line">
                    {renderPreviewLine(seg.raw)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </>
  );
}

function renderPreviewLine(rawLine: string) {
  const line = emojify(rawLine);
  const parts = line.split(SCRIPT_TOKEN_REGEX).filter(Boolean);
  return parts.map((p, j) => {
    const ph = p.match(PLACEHOLDER_INNER);
    if (ph) {
      return (
        <span key={j} className="text-lemonade font-bold">
          {ph[1]}
        </span>
      );
    }
    const br = p.match(BRACKET_INNER);
    if (br) {
      return (
        <span key={j} className="text-orange-400 font-bold">
          {br[1]}
        </span>
      );
    }
    return <span key={j}>{p}</span>;
  });
}
